import axios, { AxiosError } from "axios";
import { loginCredentials, AuthDataDto } from "./auth.types";
import {
  API_URL_USERS,
  API_URL_AUTH,
  API_URL_GOOGLE,
  API_URL_MICROSOFT,
  AvailableAPIProvidersType,
  API_URL_APPLE,
} from "../apiURLS";

let isLoggingOut = false;

// For every request that starts a session: the API runs on another origin, so
// without withCredentials the browser drops the refreshToken/deviceId cookies
// and the user is logged out once the access token expires (15 min)
const sessionRequestConfig = {
  withCredentials: true,
  withXSRFToken: true,
};

// Asynchronous function to register a user
const register = async (userData: loginCredentials): Promise<AuthDataDto> => {
  isLoggingOut = false;

  try {
    const response = await axios.post(
      `${API_URL_USERS}/register`,
      userData,
      sessionRequestConfig,
    );

    console.log(response.data);

    if (response.data) {
      localStorage.setItem("daex-auth-token", response.data.token);
      return response.data;
    } else throw new Error("Registration failed, no data received.");
  } catch (error) {
    console.error("Registration failed:", error);
    throw error; // Re-throw the error to be handled by the caller if necessary
  }
};

const login = async (userData: loginCredentials): Promise<AuthDataDto> => {
  isLoggingOut = false;
  const response = await axios.post(API_URL_AUTH + "/login", userData, {
    withCredentials: true, // Ensure cookies (refresh token) are stored
    withXSRFToken: true, // Include XSRF token
  });

  if (response.data?.token) {
    // Store only the access token in sessionStorage
    localStorage.setItem("daex-auth-token", response.data.token);
  }
  return response.data;
};

/**
 * The session is really gone (bad/expired refresh token) - as opposed to a
 * temporary problem (offline, 429, server restarting) worth retrying.
 */
export const isSessionInvalidStatus = (status?: number) =>
  status === 400 || status === 401 || status === 403;

// The backend rotates the refresh token on every refresh. Two refreshes at
// the same time (app start, several sockets, focus + visibilitychange) would
// race: the second one sends the already replaced token and fails, which
// logged the user out. So all callers share one request.
let refreshInFlight: Promise<AuthDataDto> | null = null;

const requestRefresh = async (): Promise<AuthDataDto> => {
  // A login while this request runs stores a new token - a failure here must
  // not remove that one, only the token the refresh started with
  const tokenAtStart = localStorage.getItem("daex-auth-token");
  try {
    const response = await axios.post(
      `${API_URL_AUTH}/refresh`,
      {},
      { withCredentials: true },
    );

    const { accessToken } = response.data;
    if (accessToken) {
      localStorage.setItem("daex-auth-token", accessToken);
    }

    return response.data;
  } catch (error: any) {
    // Only drop the token when the session is really invalid; on a temporary
    // error the next attempt may still succeed
    if (
      error instanceof AxiosError &&
      isSessionInvalidStatus(error.response?.status)
    ) {
      if (localStorage.getItem("daex-auth-token") === tokenAtStart) {
        localStorage.removeItem("daex-auth-token");
      }
    } else {
      console.warn("A token refresh is not possible right now");
    }
    // Still throw the error so it can be handled upstream
    throw error;
  }
};

const refreshAccess = (): Promise<AuthDataDto> => {
  if (isLoggingOut) {
    return Promise.reject("User is logging out");
  }
  if (!refreshInFlight) {
    refreshInFlight = requestRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

const googleIDLogin = async (idToken: string) => {
  isLoggingOut = false;
  const response = await axios.post(
    API_URL_GOOGLE + "/id-login",
    { idToken: idToken },
    sessionRequestConfig,
  );
  if (response.data) {
    localStorage.setItem("daex-auth-token", response.data.token);
  }
  return response.data;
};

const loginWithAPI = async (
  providerToken: string,
  provider: AvailableAPIProvidersType,
  codeVerifier: string,
  redirectUri?: string,
) => {
  isLoggingOut = false;

  let response: any;
  switch (provider) {
    case "google":
      {
        response = await axios.post(
          API_URL_GOOGLE + "/login",
          {
            providerToken: providerToken,
            codeVerifier: codeVerifier,
            redirectUri,
          },
          sessionRequestConfig,
        );
      }
      break;
    case "apple":
      {
        response = await axios.post(
          API_URL_APPLE + "/login",
          {
            providerToken: providerToken,
            codeVerifier: codeVerifier,
          },
          sessionRequestConfig,
        );
      }
      break;
    case "microsoft":
      {
        response = await axios.post(
          API_URL_MICROSOFT + "/login",
          {
            providerToken: providerToken,
            codeVerifier: codeVerifier,
            redirectUri,
          },
          sessionRequestConfig,
        );
      }
      break;
    default:
      return [];
  }
  if (response.data) {
    localStorage.setItem("daex-auth-token", response.data.token);
  }
  return response.data;
};

// Links a Google/Apple sign-in to an existing unverified account, either with
// the account password or by dropping the password
const linkSocialAccount = async (data: {
  linkToken: string;
  password?: string;
  dropPassword?: boolean;
}): Promise<AuthDataDto> => {
  isLoggingOut = false;
  const response = await axios.post(API_URL_AUTH + "/link-social", data, {
    withCredentials: true,
    withXSRFToken: true,
  });
  if (response.data?.token) {
    localStorage.setItem("daex-auth-token", response.data.token);
  }
  return response.data;
};

const logout = async () => {
  try {
    isLoggingOut = true;

    const authToken = localStorage.getItem("daex-auth-token");
    localStorage.removeItem("daex-auth-token"); // Clear token
    localStorage.removeItem("authData");

    await axios.post(
      API_URL_AUTH + "/logout",
      {},
      {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        withCredentials: true,
        withXSRFToken: true, // Include XSRF token
      },
    );
    // Optionally redirect the user
  } catch (error) {
    console.error("Logout failed:", error);
  }
};

const authService = {
  register,
  logout,
  login,
  googleIDLogin,
  refreshAccess,
  loginWithAPI,
  linkSocialAccount,
};

export default authService;
